import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const textureMaskedTypeSkill: CodeoSkill = {
    name: SKILL_NAMES.textureMaskedType,
    kind: 'codeo',
    description:
        'Cut a word out of a moving texture so the type becomes a window onto something behind it ' +
        '— for a title that should carry material rather than colour, or for the key words of a ' +
        'spoken line cut from one drifting brand gradient.',
    documentUsesSkill: check(
        (script, markup) =>
            /background-clip:\s*text|backgroundClip/.test(markup) &&
            /backgroundPosition|background-position/.test(script),
    ),
    instructions: `# Texture Masked Type

The letters are not painted; they are a hole. Behind them a texture drifts — a gradient field, a pattern, a grain — and it shows only inside the type. Because the material moves and the word does not, the headline stays perfectly readable while never being static.

It solves the most common failure in a title card: the type lands and the frame dies. Here the type lands and the *inside* of it keeps moving.

## Markup

The mask is the text; the moving layer sits behind it and is clipped to it. \`background-clip: text\` is the cheapest version and needs the text colour to be transparent.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <div id="masked-title" class="text-center" style="font-family: var(--l5-font-0), sans-serif; font-size: 220px; font-weight: 900; letter-spacing: -0.02em; line-height: 1; color: transparent; -webkit-background-clip: text; background-clip: text; background-image: linear-gradient(115deg, var(--l5-color-2, #38bdf8) 0%, var(--l5-color-1, #101820) 45%, var(--l5-color-2, #38bdf8) 100%); background-size: 300% 100%; background-position: 0% 0%;">MOMENTUM</div>
</div>
\`\`\`

## Timeline code

Move the *background*, not the element. The word stays exactly where it is while the material travels through it.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('#masked-title', { opacity: 0, backgroundPosition: '0% 0%' });

tl.to('#masked-title', { opacity: 1, duration: 0.7, ease: 'power3.out' }, 0.2);
tl.to('#masked-title', {
    backgroundPosition: '100% 0%',
    duration: 5.2,
    ease: 'none',
}, 0.2);

window.__timelines = { main: tl };
\`\`\`

\`ease: 'none'\` over a long duration is what makes the material read as continuously present rather than as an effect that fires and stops.

## Variant: the key words of a line

The same mask works as emphasis inside a spoken line: the word that carries it ("video", "Lumen5", "Minutes") is cut from the brand gradient while the rest of the line stays ink. When two or three neighbouring words share the treatment, **one gradient spans all of them**, so the colour runs from the first letter of the first word to the last letter of the last, as if they were one shape. While the line holds, the gradient slides a little and its pink end drifts through the letters.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center"><div id="line" style="text-align: center; white-space: nowrap; line-height: 1; font-family: var(--l5-font-0), sans-serif; font-size: 130px; font-weight: 500; color: var(--l5-color-0, #14121f);"><span class="w">it</span> <span class="w">gives</span> <span class="w">you</span> <span class="w">back</span> <span class="w">a</span> <span class="w sweep" style="font-weight: 700;">video</span></div></div>
\`\`\`

\`\`\`css
.w { display: inline-block; }
.sweep {
  color: transparent;
  -webkit-background-clip: text;
  background-clip: text;
  background-image: linear-gradient(90deg, var(--l5-color-3, #ea64a2) 0%, var(--l5-color-3, #ea64a2) 20%, #9a5cdc 60%, var(--l5-color-2, #7257f2) 100%);
  background-repeat: no-repeat;
}
\`\`\`

Size the gradient to the span of all the treated words, then give each word the slice of it under its own letters. The sweep moves every slice by the same amount.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 1.6;
const HOLD_START = 0.1;     // when the treated words land
const SWEEP = 0.3;          // share of the gradient's width the colour drifts by while the line holds

const swept = gsap.utils.toArray('#line .sweep');
const left = Math.min(...swept.map((word) => word.offsetLeft));
const right = Math.max(...swept.map((word) => word.offsetLeft + word.offsetWidth));
const span = right - left;
// Twice as wide as the words, so it has room to slide.
swept.forEach((word) => {
  word.style.backgroundSize = \`\${span * 2}px 100%\`;
  word.style.backgroundPosition = \`\${left - word.offsetLeft - span}px 0px\`;
});
tl.to(swept, {
  backgroundPosition: (i, word) => \`\${left - word.offsetLeft - span + span * 2 * SWEEP}px 0px\`,
  duration: SCENE - HOLD_START, ease: 'power1.out',
}, HOLD_START);

tl.set({}, {}, SCENE);
window.__timelines = { main: tl };
\`\`\`

- **One word or several.** One word gets the whole gradient; two or three neighbouring words share it. More than three is a gradient line, not an emphasis.
- **One gradient across the words.** A gradient per word restarts its colours at every word, and the line reads as a row of stickers.
- **Ground.** For light and dark flat grounds. On a \`${SKILL_NAMES.driftingMeshGround}\` ground the treated word is white.
- **With an underline.** The rule under a swept word takes the gradient's violet end (\`${SKILL_NAMES.underlineDraw}\`).
- **Measure after the fonts load.** If the face loads late, run the sizing again in \`document.fonts.ready.then(...)\` so the slices still line up.

## Adapt

- **Material**: a two-stop gradient reads as a sheen; a three-stop palette gradient reads as a print. Keep the material soft: hard-edged stripes cut the letters into slivers and read as a glitch, not a material.
- **Speed**: the background should cross in 4–6s. Faster reads as a shimmer effect; slower and it appears not to move at all.
- **Photographic fill**: an image behind the mask makes the word a window onto a scene. Powerful, and only worth it when the image means something.
- **Grain inside the type**: layering a subtle noise over the gradient gives the letters material weight — see \`${SKILL_NAMES.grainAndBloom}\`.
- **Entrance**: pair with a tracking close or a fade for the arrival, moving the masked element as a whole, and let the texture run underneath — see \`${SKILL_NAMES.letterSpacingSettle}\`.
- **Knockout instead**: inverting it — solid type punched out of a filled block — is the same idea with the opposite figure/ground, and reads heavier.

## Pitfalls

- **The clip belongs to the element that holds the letters.** \`background-clip: text\` paints only the element's own text, not text inside children that are transformed, \`inline-block\` or \`overflow: hidden\`. Splitting the masked word into moving letter spans leaves the word blank, or shows one letter. To move words separately, give each word span its own clip with the background sliced across them, as in the key-word variant.
- The text colour must be \`transparent\` or the fill paints over the mask and nothing shows through.
- Set both \`-webkit-background-clip\` and \`background-clip\`; one alone silently no-ops in one renderer.
- \`background-size\` must exceed 100% on the axis being moved, or there is nothing to travel and \`backgroundPosition\` does nothing visible.
- The style guidance calls out gradient text as a generic default. This technique earns it only when the material is *moving* and is part of the idea — a static gradient headline is exactly the tell it warns about.
- Keep the word short and the weight heavy. Thin letterforms give the texture no room to be seen and the effect vanishes.
- Contrast is now variable across the word: check the *lowest*-contrast moment of the sweep, not just the first frame.
- One masked headline per video. Two makes it a template. The key-word variant is the exception: one treated phrase per graphic.`,
};
